import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapChart, ScatterChart } from 'echarts/charts';
import { GeoComponent, TooltipComponent, VisualMapComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import formatCurrency from '../../../../utils/formatCurrency';
import { getAppMainScrollElement } from '../../../../utils/appMainScroll';
import { buildChinaMapStyle, buildChinaMapVisualRange, getPrimaryPhaseChartTheme } from '../../charts/pipeline/lib/orderPrimaryPhaseChartTheme';
import {
	buildChinaMapMetricSeriesData,
	buildChinaMapMetricVisualRange,
	buildChinaMapProvinceRows,
	buildChinaMapSeriesData,
	buildOrderCityBuckets,
	computeCustomerGeoViewport,
	computeProvinceCityGeoViewport,
	computeSingleCityGeoViewport,
	enrichGeoBucketsFromApi,
	filterCityRowsByProvince,
	findCityRowByCode,
	isChinaMapRegionClick,
	resolveMapSlotStyle,
	resolveWideInlineGeoLayout,
	resolveInlineVisualMapLayout,
} from '../../charts/pipeline/lib/orderPrimaryPhaseGeoBuckets';
import { getPrimaryPhaseRowMetricValue, isPrimaryPhaseCurrencyMetric } from '../../charts/pipeline/lib/orderPrimaryPhaseMetric';
import useStatisticsChartTheme from './useStatisticsChartTheme';
import useStatisticsMapContainerResize from './useStatisticsMapContainerResize';

echarts.use([TooltipComponent, GeoComponent, MapChart, ScatterChart, VisualMapComponent, CanvasRenderer]);

export { echarts };

const CHINA_GEO_PUBLIC_PATH = `${process.env.PUBLIC_URL || ''}/geo/china-full.json`;
const CHINA_GEO_CDN_URL = 'https://geo.datav.aliyun.com/areas_v3/bound/100000_full.json';
const CHINA_GEO_SOURCES = [CHINA_GEO_PUBLIC_PATH, CHINA_GEO_CDN_URL];
export const CHINA_MAP_DEFAULT_HEIGHT = '22rem';
const MAP_BORDER_WIDTH = 0.8;
const MAP_HIGHLIGHT_BORDER_WIDTH = 1.65;
const CITY_SYMBOL_SIZE = 7;
const CITY_SYMBOL_SIZE_PROVINCE = 8;
const CITY_SYMBOL_SIZE_FOCUSED = 11;

const EMPTY_MAP_FOCUS = { provinceName: null, cityCode: null };

let chinaMapLoadPromise = null;

const loadChinaMap = async () => {
	if (echarts.getMap('china')) return;
	if (chinaMapLoadPromise) return chinaMapLoadPromise;

	chinaMapLoadPromise = (async () => {
		let lastError;
		for (const url of CHINA_GEO_SOURCES) {
			try {
				const res = await fetch(url);
				if (!res.ok) throw new Error('地图数据加载失败');
				const geoJson = await res.json();
				echarts.registerMap('china', geoJson);
				return;
			} catch (error) {
				lastError = error;
			}
		}
		chinaMapLoadPromise = null;
		throw lastError ?? new Error('地图数据加载失败');
	})();

	return chinaMapLoadPromise;
};

const resolveMapFocusContext = ({ focus, cityRows, compact, geoViewport }) => {
	const focusedCityRow = findCityRowByCode(cityRows, focus.cityCode);
	const activeCityRows = focusedCityRow ? [focusedCityRow] : focus.provinceName ? filterCityRowsByProvince(cityRows, focus.provinceName) : cityRows;
	const viewport = focusedCityRow
		? computeSingleCityGeoViewport(focusedCityRow, { compact })
		: focus.provinceName
			? computeProvinceCityGeoViewport(cityRows, focus.provinceName, { compact })
			: geoViewport;
	const activeProvinceName = focus.provinceName ?? focusedCityRow?.provinceMapName ?? null;
	const isFocused = Boolean(focus.provinceName || focus.cityCode);

	return { focusedCityRow, activeCityRows, viewport, activeProvinceName, isFocused };
};

const buildScatterSeriesPatch = ({ activeCityRows, focus, compact, isLight, pointColor, pointShadow }) => {
	const { cityCode, provinceName } = focus;

	return {
		type: 'scatter',
		coordinateSystem: 'geo',
		geoIndex: 0,
		zlevel: 2,
		animationDurationUpdate: 0,
		triggerEvent: true,
		cursor: 'pointer',
		data: activeCityRows.map((row) => ({
			name: row.cityName,
			value: [...row.coord, row.count],
			row,
		})),
		symbolSize: (value, params) => {
			const count = Array.isArray(value) ? value[2] : 0;
			const isFocusedPoint = cityCode && params?.data?.row?.cityCode === cityCode;
			const base = isFocusedPoint ? CITY_SYMBOL_SIZE_FOCUSED : provinceName ? CITY_SYMBOL_SIZE_PROVINCE : CITY_SYMBOL_SIZE;
			const maxSize = isFocusedPoint ? 14 : provinceName ? 11 : 9;
			return Math.min(base + Math.log2(Math.max(count, 1)) * 1, maxSize);
		},
		label:
			cityCode || provinceName
				? {
						show: true,
						formatter: (params) => params.data?.row?.cityName ?? params.name ?? '',
						color: isLight ? '#344050' : '#e8edf5',
						fontSize: cityCode ? (compact ? 10 : 11) : compact ? 9 : 10,
						fontWeight: cityCode ? 600 : 500,
						position: 'top',
						distance: cityCode ? 6 : 4,
					}
				: { show: false },
		itemStyle: {
			color: pointColor,
			shadowBlur: cityCode ? 10 : provinceName ? 8 : 6,
			shadowColor: pointShadow,
		},
		emphasis: {
			scale: 1.25,
			label: {
				show: true,
				formatter: (params) => params.data?.row?.cityName ?? params.name ?? '',
				color: isLight ? '#344050' : '#e8edf5',
				fontSize: compact ? 9 : 10,
				position: 'top',
			},
		},
	};
};

const useStatisticsChinaMapChart = ({
	orders = [],
	geoAggregates = null,
	highlightProvinceNames = [],
	highlightAnchorCityRows = [],
	provinceRowsOverride = null,
	provinceMetric = null,
	isLoading = false,
	animationKey = 'default',
	height = CHINA_MAP_DEFAULT_HEIGHT,
	compact = false,
	fillSlot = false,
	fitWideSlot = false,
	enableOrderDrill = false,
	onDrillSelect,
	resolveLoadingStyle,
}) => {
	const isProvinceMetricMode = Boolean(provinceMetric);
	const { isLight } = useStatisticsChartTheme();
	const mapWrapRef = useRef(null);
	const chartRef = useRef(null);
	const focusRef = useRef(EMPTY_MAP_FOCUS);
	const mapContextRef = useRef(null);
	const lastDrillClickRef = useRef({ key: '', at: 0 });
	const suppressRegionClickUntilRef = useRef(0);
	const [mapReady, setMapReady] = useState(Boolean(echarts.getMap('china')));
	const [mapError, setMapError] = useState(false);
	const [showBack, setShowBack] = useState(false);

	useEffect(() => {
		let cancelled = false;
		loadChinaMap()
			.then(() => {
				if (!cancelled) setMapReady(true);
			})
			.catch(() => {
				if (!cancelled) setMapError(true);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		const wrap = mapWrapRef.current;
		if (!wrap || !mapReady) return undefined;

		const onWheel = (event) => {
			const scrollEl = getAppMainScrollElement();
			if (!scrollEl) return;
			scrollEl.scrollTop += event.deltaY;
			event.preventDefault();
			event.stopPropagation();
		};

		wrap.addEventListener('wheel', onWheel, { passive: false, capture: true });
		return () => wrap.removeEventListener('wheel', onWheel, { capture: true });
	}, [mapReady]);

	const chartTheme = useMemo(() => getPrimaryPhaseChartTheme(isLight), [isLight]);
	const mapStyle = useMemo(() => buildChinaMapStyle(isLight), [isLight]);

	const {
		rows: cityRows,
		provinceRows: bucketProvinceRows,
		unknownCount,
	} = useMemo(() => {
		if (isProvinceMetricMode) {
			return {
				rows: buildOrderCityBuckets(orders).rows,
				provinceRows: [],
				unknownCount: 0,
			};
		}

		const fromApi = enrichGeoBucketsFromApi(geoAggregates);
		if ((fromApi.rows?.length ?? 0) > 0 || (geoAggregates?.cityBuckets?.length ?? 0) > 0) {
			return fromApi;
		}
		if (orders.length > 0) return buildOrderCityBuckets(orders);
		return fromApi;
	}, [geoAggregates, isProvinceMetricMode, orders]);

	const sourceProvinceRows = provinceRowsOverride ?? bucketProvinceRows;
	const mapProvinceRows = useMemo(
		() => (isProvinceMetricMode ? sourceProvinceRows : buildChinaMapProvinceRows(sourceProvinceRows, highlightProvinceNames)),
		[highlightProvinceNames, isProvinceMetricMode, sourceProvinceRows],
	);
	const hasCustomerProvinceHighlight = !isProvinceMetricMode && highlightProvinceNames.length > 0;
	const currencyMetric = isProvinceMetricMode && isPrimaryPhaseCurrencyMetric(provinceMetric);
	const visualRange = useMemo(() => {
		if (isProvinceMetricMode) {
			return buildChinaMapMetricVisualRange(mapProvinceRows, provinceMetric);
		}
		return buildChinaMapVisualRange(mapProvinceRows);
	}, [isProvinceMetricMode, mapProvinceRows, provinceMetric]);
	const mapSeriesData = useMemo(() => {
		if (isProvinceMetricMode) {
			return buildChinaMapMetricSeriesData(mapProvinceRows, provinceMetric, { useRank: visualRange.useRank });
		}
		return buildChinaMapSeriesData(sourceProvinceRows, {
			useRank: visualRange.useRank,
			highlightProvinceNames,
		});
	}, [highlightProvinceNames, isProvinceMetricMode, mapProvinceRows, provinceMetric, sourceProvinceRows, visualRange.useRank]);
	const geoViewport = useMemo(
		() => computeCustomerGeoViewport(cityRows.length > 0 ? cityRows : highlightAnchorCityRows, { compact }),
		[cityRows, compact, highlightAnchorCityRows],
	);

	const mapFocusResetKey = useMemo(() => {
		if (isProvinceMetricMode) {
			return `${animationKey}|${provinceMetric}|${orders.length}|${mapProvinceRows.map((row) => row.name).join('\u0000')}`;
		}
		return `${animationKey}|${highlightProvinceNames.join('\u0000')}|${orders.length}|${geoAggregates ? 'geo-api' : 'orders'}`;
	}, [animationKey, geoAggregates, highlightProvinceNames, isProvinceMetricMode, mapProvinceRows, orders.length, provinceMetric]);

	const applyDrillToChart = useCallback((chart, focus = focusRef.current) => {
		if (!chart || chart.isDisposed?.()) return;
		const ctx = mapContextRef.current;
		if (!ctx) return;

		if (ctx.isProvinceMetricMode) {
			const provinceName = focus.provinceName;
			const isFocused = Boolean(provinceName);
			const activeGeoViewport = provinceName
				? computeProvinceCityGeoViewport(ctx.cityRows, provinceName, { compact: ctx.compact })
				: ctx.geoViewport;
			const { borderColor, highlightBorder, emphasisArea, emphasisBorder } = ctx.mapStyle;

			chart.setOption(
				{
					geo: {
						center: activeGeoViewport.center ?? undefined,
						zoom: activeGeoViewport.zoom,
						animationDurationUpdate: 0,
						scaleLimit: {
							min: 0.85,
							max: activeGeoViewport.maxRoamZoom ?? (ctx.compact ? 6.5 : 5.8),
						},
					},
					visualMap: { show: ctx.mapProvinceRows.length > 0 && !isFocused },
					series: [
						{
							type: 'map',
							map: 'china',
							geoIndex: 0,
							selectedMode: false,
							triggerEvent: true,
							cursor: 'pointer',
							data: ctx.mapSeriesData.map((item) => ({
								name: item.name,
								value: item.value,
								row: item.row,
								itemStyle: {
									borderColor: provinceName === item.name ? highlightBorder : borderColor,
									borderWidth: provinceName === item.name ? MAP_HIGHLIGHT_BORDER_WIDTH : MAP_BORDER_WIDTH,
									opacity: isFocused && provinceName !== item.name ? 0.35 : 1,
								},
							})),
							itemStyle: {
								borderColor,
								borderWidth: MAP_BORDER_WIDTH,
							},
							emphasis: {
								label: {
									show: true,
									color: ctx.isLight ? '#344050' : '#fff',
									fontSize: 10,
								},
								itemStyle: {
									areaColor: emphasisArea,
									borderColor: emphasisBorder,
								},
							},
						},
					],
				},
				{ lazyUpdate: false },
			);
			return;
		}

		const { activeCityRows, viewport, activeProvinceName, isFocused } = resolveMapFocusContext({
			focus,
			cityRows: ctx.cityRows,
			compact: ctx.compact,
			geoViewport: ctx.geoViewport,
		});
		const { borderColor, highlightBorder, pointColor, pointShadow, emphasisArea, emphasisBorder } = ctx.mapStyle;

		chart.setOption(
			{
				geo: {
					center: viewport.center ?? undefined,
					zoom: viewport.zoom,
					animationDurationUpdate: 0,
					scaleLimit: {
						min: 0.85,
						max: viewport.maxRoamZoom ?? (ctx.compact ? 4.2 : 3.5),
					},
				},
				visualMap: { show: ctx.mapProvinceRows.length > 0 && !isFocused },
				series: [
					{
						type: 'map',
						map: 'china',
						geoIndex: 0,
						selectedMode: false,
						animationDurationUpdate: 0,
						triggerEvent: true,
						cursor: 'pointer',
						silent: Boolean(focus.cityCode),
						data: ctx.mapSeriesData.map((item) => ({
							name: item.name,
							value: item.value,
							row: item.row,
							itemStyle: {
								borderColor: activeProvinceName === item.name || item.isHighlight ? highlightBorder : borderColor,
								borderWidth: activeProvinceName === item.name ? MAP_HIGHLIGHT_BORDER_WIDTH + 0.35 : item.isHighlight ? MAP_HIGHLIGHT_BORDER_WIDTH : MAP_BORDER_WIDTH,
								opacity: isFocused && activeProvinceName !== item.name ? 0.35 : 1,
							},
						})),
						itemStyle: {
							borderColor,
							borderWidth: MAP_BORDER_WIDTH,
						},
						emphasis: {
							label: {
								show: !focus.cityCode,
								color: ctx.isLight ? '#344050' : '#fff',
								fontSize: 10,
							},
							itemStyle: {
								areaColor: emphasisArea,
								borderColor: emphasisBorder,
							},
						},
					},
					buildScatterSeriesPatch({
						activeCityRows,
						focus,
						compact: ctx.compact,
						isLight: ctx.isLight,
						pointColor,
						pointShadow,
					}),
				],
			},
			{ lazyUpdate: false },
		);
	}, []);

	useEffect(() => {
		mapContextRef.current = {
			cityRows,
			compact,
			geoViewport,
			mapProvinceRows,
			mapSeriesData,
			mapStyle,
			isLight,
			isProvinceMetricMode,
			provinceMetric,
		};
	}, [cityRows, compact, geoViewport, isLight, isProvinceMetricMode, mapProvinceRows, mapSeriesData, mapStyle, provinceMetric]);

	useEffect(() => {
		focusRef.current = EMPTY_MAP_FOCUS;
		lastDrillClickRef.current = { key: '', at: 0 };
		suppressRegionClickUntilRef.current = 0;
		setShowBack(false);
		const chart = chartRef.current?.getEchartsInstance?.();
		if (chart) {
			applyDrillToChart(chart, EMPTY_MAP_FOCUS);
		}
	}, [applyDrillToChart, mapFocusResetKey]);

	const baseMapOption = useMemo(() => {
		const { baseArea: baseAreaColor, border: borderColor, provinceGradient, point: pointColor, emphasisArea, emphasisBorder, highlightBorder, pointShadow } = mapStyle;
		const { activeCityRows, viewport } = resolveMapFocusContext({
			focus: EMPTY_MAP_FOCUS,
			cityRows,
			compact,
			geoViewport,
		});
		const activeViewport = isProvinceMetricMode ? geoViewport : viewport;
		const roamScaleMax = activeViewport.maxRoamZoom ?? (compact ? (isProvinceMetricMode ? 6.5 : 4.2) : isProvinceMetricMode ? 5.8 : 3.5);

		const geoConfig = {
			map: 'china',
			roam: compact ? false : true,
			silent: compact,
			animationDurationUpdate: 0,
			scaleLimit: { min: 0.85, max: roamScaleMax },
			zoom: activeViewport.zoom,
			label: { show: false },
			itemStyle: {
				areaColor: baseAreaColor,
				borderColor,
				borderWidth: MAP_BORDER_WIDTH,
			},
		};

		if (activeViewport.center) {
			geoConfig.center = activeViewport.center;
		}

		if (fitWideSlot && !isProvinceMetricMode) {
			Object.assign(geoConfig, resolveWideInlineGeoLayout());
		}

		const visualMapLayout = resolveInlineVisualMapLayout(compact);
		const visualMapText = isProvinceMetricMode
			? [visualRange.useRank ? '高' : currencyMetric ? '高' : '多', visualRange.useRank ? '低' : currencyMetric ? '低' : '少']
			: [visualRange.useRank ? '高' : '多', visualRange.useRank ? '低' : '少'];

		const mapSeries = {
			type: 'map',
			map: 'china',
			geoIndex: 0,
			selectedMode: false,
			animationDurationUpdate: 0,
			triggerEvent: true,
			cursor: 'pointer',
			data: mapSeriesData.map((item) => ({
				name: item.name,
				value: item.value,
				row: item.row,
				itemStyle: {
					borderColor: item.isHighlight ? highlightBorder : borderColor,
					borderWidth: item.isHighlight ? MAP_HIGHLIGHT_BORDER_WIDTH : MAP_BORDER_WIDTH,
				},
			})),
			itemStyle: {
				borderColor,
				borderWidth: MAP_BORDER_WIDTH,
			},
			emphasis: {
				label: {
					show: true,
					color: isLight ? '#344050' : '#fff',
					fontSize: 10,
				},
				itemStyle: {
					areaColor: emphasisArea,
					borderColor: emphasisBorder,
				},
			},
		};

		const series = isProvinceMetricMode
			? [mapSeries]
			: [
					mapSeries,
					buildScatterSeriesPatch({
						activeCityRows,
						focus: EMPTY_MAP_FOCUS,
						compact,
						isLight,
						pointColor,
						pointShadow,
					}),
				];

		return {
			backgroundColor: 'transparent',
			tooltip: {
				...chartTheme.tooltip,
				trigger: 'item',
				formatter: (params) => {
					if (isProvinceMetricMode) {
						if (!params?.name) return '';
						const row = mapSeriesData.find((item) => item.name === params.name)?.row;
						if (!row) return `${params.name}<br/>暂无订单`;
						const detail = `${params.name}<br/>${row.count} 单 · ${formatCurrency(row.totalPrice)}`;
						if (!focusRef.current.provinceName) {
							return `${detail}<br/><span style="opacity:.72;font-size:10px">点击放大到省份</span>`;
						}
						return detail;
					}

					const focus = focusRef.current;
					if (params?.seriesType === 'scatter') {
						const row = params.data?.row;
						if (!row) return params.name ?? '';
						const detail = `${row.cityName}<br/>${row.provinceMapName}<br/>${row.count} 单 · ${formatCurrency(row.totalPrice)}`;
						if (!focus.cityCode) {
							return `${detail}<br/><span style="opacity:.72;font-size:10px">点击放大到城市</span>`;
						}
						return detail;
					}
					if (!params?.name) return '';
					const row = mapProvinceRows.find((item) => item.name === params.name);
					if (!row) return `${params.name}<br/>暂无订单`;
					if (row.count <= 0) {
						return `${params.name}<br/>暂无订单<br/><span style="opacity:.72;font-size:10px">请点选有订单的省份或光点城市</span>`;
					}
					if (row.count <= 0 && hasCustomerProvinceHighlight) {
						return `${params.name}<br/>已选客户所在省份`;
					}
					if (row.count > 0 && !focus.provinceName && !focus.cityCode) {
						return `${params.name}<br/>${row.count} 单 · ${formatCurrency(row.totalPrice)}<br/><span style="opacity:.72;font-size:10px">点击放大到省内城市</span>`;
					}
					return `${params.name}<br/>${row.count} 单 · ${formatCurrency(row.totalPrice)}`;
				},
			},
			visualMap: {
				show: mapProvinceRows.length > 0,
				type: 'continuous',
				min: visualRange.min,
				max: visualRange.max,
				calculable: false,
				...visualMapLayout,
				text: visualMapText,
				textStyle: { color: chartTheme.labelColor, fontSize: compact ? 9 : 10 },
				inRange: { color: provinceGradient },
				seriesIndex: 0,
			},
			geo: geoConfig,
			series,
		};
	}, [
		chartTheme,
		cityRows,
		compact,
		currencyMetric,
		fitWideSlot,
		geoViewport,
		hasCustomerProvinceHighlight,
		isLight,
		isProvinceMetricMode,
		mapProvinceRows,
		mapSeriesData,
		mapStyle,
		visualRange,
	]);

	const syncMapSize = useCallback(
		(chart) => {
			if (!chart || chart.isDisposed?.()) return;
			const chartEl = mapWrapRef.current?.querySelector('.statistics-chart-frame__chart');
			if (!chartEl) return;
			const { width, height: chartHeight } = chartEl.getBoundingClientRect();
			if (width <= 0 || chartHeight <= 0) return;

			chart.resize({ width, height: chartHeight });

			const patch = {};
			if (fitWideSlot) {
				Object.assign(patch, { geo: resolveWideInlineGeoLayout(width, chartHeight) });
			}
			if (compact) {
				patch.visualMap = resolveInlineVisualMapLayout(compact, chartHeight);
			}
			if (Object.keys(patch).length > 0) {
				chart.setOption(patch, { lazyUpdate: true });
			}
		},
		[compact, fitWideSlot],
	);

	const syncFocusedDrill = useCallback(
		(chart) => {
			if (!chart || chart.isDisposed?.()) return;
			if (focusRef.current.provinceName || focusRef.current.cityCode) {
				applyDrillToChart(chart, focusRef.current);
			}
		},
		[applyDrillToChart],
	);

	useStatisticsMapContainerResize(mapWrapRef, chartRef, {
		enabled: mapReady,
		onSynced: (chart) => {
			syncMapSize(chart);
			syncFocusedDrill(chart);
		},
	});

	const handleChartReady = useCallback(
		(chart) => {
			syncMapSize(chart);
			syncFocusedDrill(chart);
		},
		[syncFocusedDrill, syncMapSize],
	);

	const handleChartClick = useCallback(
		(params) => {
			const chart = chartRef.current?.getEchartsInstance?.();
			if (!chart) return;

			const drillKey = `${params?.seriesType ?? ''}|${params?.name ?? ''}|${params?.data?.row?.cityCode ?? ''}`;
			const now = Date.now();
			if (lastDrillClickRef.current.key === drillKey && now - lastDrillClickRef.current.at < 120) {
				return;
			}
			lastDrillClickRef.current = { key: drillKey, at: now };

			if (isProvinceMetricMode) {
				if (!isChinaMapRegionClick(params)) return;
				const row = mapSeriesData.find((item) => item.name === params.name)?.row;
				if (!row || getPrimaryPhaseRowMetricValue(row, provinceMetric) <= 0) return;
				if (enableOrderDrill && onDrillSelect) {
					onDrillSelect({
						kind: 'geo',
						provinceName: params.name,
						cityCode: null,
						label: params.name,
					});
					return;
				}
				const nextFocus = { provinceName: params.name, cityCode: null };
				focusRef.current = nextFocus;
				applyDrillToChart(chart, nextFocus);
				setShowBack(true);
				return;
			}

			if (params?.seriesType === 'scatter') {
				const row = params.data?.row;
				const cityCode = row?.cityCode;
				if (!cityCode) return;
				if (enableOrderDrill && onDrillSelect) {
					onDrillSelect({
						kind: 'geo',
						provinceName: row.provinceMapName ?? focusRef.current.provinceName,
						cityCode,
						label: row.cityName ?? row.cityCode,
					});
					return;
				}
				suppressRegionClickUntilRef.current = now + 200;
				const nextFocus = {
					provinceName: row.provinceMapName ?? focusRef.current.provinceName,
					cityCode,
				};
				focusRef.current = nextFocus;
				applyDrillToChart(chart, nextFocus);
				setShowBack(true);
				return;
			}

			if (now < suppressRegionClickUntilRef.current) return;
			if (!isChinaMapRegionClick(params)) return;
			const row = mapProvinceRows.find((item) => item.name === params.name);
			if (!row || row.count <= 0) return;
			if (enableOrderDrill && onDrillSelect) {
				onDrillSelect({
					kind: 'geo',
					provinceName: params.name,
					cityCode: null,
					label: params.name,
				});
				return;
			}
			const nextFocus = { provinceName: params.name, cityCode: null };
			focusRef.current = nextFocus;
			applyDrillToChart(chart, nextFocus);
			setShowBack(true);
		},
		[applyDrillToChart, enableOrderDrill, isProvinceMetricMode, mapProvinceRows, mapSeriesData, onDrillSelect, provinceMetric],
	);

	const shouldSetChartOption = useCallback((prevProps, nextProps) => prevProps.option !== nextProps.option, []);

	const handleBack = useCallback(() => {
		const chart = chartRef.current?.getEchartsInstance?.();
		if (!chart) return;

		focusRef.current = EMPTY_MAP_FOCUS;
		lastDrillClickRef.current = { key: '', at: 0 };
		setShowBack(false);
		applyDrillToChart(chart, EMPTY_MAP_FOCUS);
	}, [applyDrillToChart]);

	const chartEvents = useMemo(() => ({ click: handleChartClick }), [handleChartClick]);
	const frameStyle = useMemo(() => resolveMapSlotStyle(height, { fillSlot }), [fillSlot, height]);
	const loadingStyle = useMemo(
		() => (resolveLoadingStyle ? resolveLoadingStyle(height || CHINA_MAP_DEFAULT_HEIGHT, { fillSlot }) : frameStyle),
		[fillSlot, height, resolveLoadingStyle, frameStyle],
	);
	const echartStyle = useMemo(() => (fillSlot ? { width: '100%', height: '100%', minHeight: 0 } : frameStyle), [fillSlot, frameStyle]);

	const isEmpty = isProvinceMetricMode ? mapProvinceRows.length === 0 : cityRows.length === 0 && !hasCustomerProvinceHighlight;
	const isChartLoading = isLoading || !mapReady;

	return {
		mapWrapRef,
		chartRef,
		mapError,
		isChartLoading,
		isEmpty,
		unknownCount,
		showBack,
		handleBack,
		baseMapOption,
		shouldSetChartOption,
		chartEvents,
		handleChartReady,
		frameStyle,
		loadingStyle,
		echartStyle,
		compact,
		fillSlot,
		animationKey,
	};
};

export default useStatisticsChinaMapChart;
